import React, { useEffect, useState } from "react";
import "./InstallAppPrompt.css";

const DISMISS_KEY = "tl_install_prompt_dismissed_at";
const DISMISS_COOLDOWN_DAYS = 14;

// Chrome/Edge/Android fire `beforeinstallprompt` only when the browser
// itself has decided the site qualifies as installable (manifest + service
// worker already present — see public/manifest.json and
// serviceWorkerRegistration.js) — this just captures that moment and shows
// an obvious, on-brand "Install" affordance instead of relying on users to
// notice the browser's own tucked-away menu item. Safari/iOS never fires
// this event at all, so the prompt simply never appears there — no native
// install flow exists to hook into on that platform.
const InstallAppPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    const cooldownElapsed =
      Date.now() - dismissedAt > DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

    const handleBeforeInstall = (e) => {
      e.preventDefault(); // suppress the browser's own mini-infobar
      if (!cooldownElapsed) return;
      setDeferredPrompt(e);
      setVisible(true);
    };

    const handleInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice; // resolves once the user accepts/declines
    // A given beforeinstallprompt event can only be used once either way.
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="install-prompt" role="dialog" aria-label="Install TutorsLinks">
      <span className="install-prompt-icon" aria-hidden="true">
        📲
      </span>
      <div className="install-prompt-text">
        <strong>Install TutorsLinks</strong>
        <span>App-like access from your home screen — faster, no browser bar.</span>
      </div>
      <div className="install-prompt-actions">
        <button
          type="button"
          className="install-prompt-btn install-prompt-btn-primary"
          onClick={handleInstallClick}
        >
          Install
        </button>
        <button
          type="button"
          className="install-prompt-btn install-prompt-btn-dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss install prompt"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

export default InstallAppPrompt;
